import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Award, Download, ExternalLink } from "lucide-react";
import { PortalPage, Btn, Card, Spinner, ErrorNote } from "@/components/PortalPage";
import { lsoTrack, qs, downloadPdf, fmtDate } from "@/lib/lso";
import { useAuth } from "@/context/AuthContext";

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between gap-4 border-b border-[#2a2a2a] py-2.5 text-sm last:border-0">
    <span className="text-neutral-400">{label}</span><span className="text-right font-semibold" data-testid={testId}>{value}</span>
  </div>
);

export default function LsoCertificate({ kind = "staff" }) {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const as = kind === "rental" ? "me" : params.get("as") || "me";
  const track = lsoTrack(kind, as);
  const [cert, setCert] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    track.getCertificate().then(setCert).catch((e) => setErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, as]);

  const number = cert?.certificate_number || cert?.number;
  const expired = cert?.is_expired ?? (cert?.expires_at && new Date(cert.expires_at) < new Date());

  return (
    <PortalPage eyebrow="LASER SAFETY OFFICER" title="Your Certificate" back={`${track.hubPath}${qs(as)}`} backLabel="Course hub" testId="lso-certificate-page">
      {err && <ErrorNote>{err}</ErrorNote>}
      {!cert && !err && <Spinner />}
      {cert && (
        <>
          <Card className="relative overflow-hidden p-8" style={{ borderColor: "var(--accent)" }}>
            <Award size={44} className="text-[var(--accent)]" />
            <p className="mt-4 text-[11px] font-bold tracking-[0.3em] text-neutral-400">CERTIFICATE NUMBER</p>
            <p className="font-mono text-2xl font-bold tracking-wider" data-testid="cert-number">{number}</p>
            <p className={`mt-2 inline-block rounded-full px-3 py-1 text-[11px] font-extrabold tracking-wider ${expired ? "bg-[#FF3333]/15 text-[#FF3333]" : "bg-[var(--accent)]/15 text-[var(--accent)]"}`} data-testid="cert-status">{expired ? "EXPIRED" : "VALID"}</p>
          </Card>
          <Card>
            {(cert.learner_name || kind === "rental") && <Row label="Learner" value={cert.learner_name || user.full_name || user.email} testId="cert-learner" />}
            {cert.course_title && <Row label="Course" value={cert.course_title} />}
            {cert.standard && <Row label="Standard" value={cert.standard} />}
            <Row label="Score" value={`${Math.round(cert.score_percent)}%`} testId="cert-score" />
            <Row label="Issued" value={fmtDate(cert.issued_at)} />
            <Row label="Expires" value={fmtDate(cert.expires_at)} testId="cert-expires" />
          </Card>
          {cert.pdf_base64 && <Btn onClick={() => downloadPdf(cert.pdf_base64, `${String(number).startsWith("ALR-LSO") ? number : `ALR-LSO-${number}`}.pdf`)} data-testid="cert-download"><Download size={16} /> Download PDF</Btn>}
          <Btn variant="outline" onClick={() => window.open(`/lso-verify/${encodeURIComponent(number)}`, "_blank")} data-testid="cert-verify-link"><ExternalLink size={16} /> Public verification page</Btn>
        </>
      )}
    </PortalPage>
  );
}
